import { FileType } from '../types';
import { 
  FileText, Image, Video, FileAudio, FileSpreadsheet, 
  Code, File, FileArchive, FileJson, FileCode2,
  Presentation, FileType2, Database, Settings, Terminal,
  BookOpen, Layers, Box, Clapperboard, Music, ImageIcon,
  FileCheck, FileCog, FileKey, FileSearch, FilePlus2,
  LucideIcon
} from 'lucide-react';

// 扩展的文件类型
export type ExtendedFileType = FileType | 'archive' | 'font' | 'cad' | 'design' | 'database' | 'config' | 'ebook' | '3d';

// 根据文件扩展名获取更精确的图标
export const getFileIconByExtension = (filename: string): LucideIcon => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  
  // 压缩包
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'].includes(ext)) {
    return FileArchive;
  }
  
  // PDF
  if (ext === 'pdf') {
    return FileText;
  }
  
  // Word 文档
  if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) {
    return FileText;
  }
  
  // Excel 表格
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return FileSpreadsheet;
  }
  
  // PPT 演示文稿
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) {
    return Presentation;
  }
  
  // 图片
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico', 'tiff', 'tif', 'psd', 'ai', 'eps', 'raw', 'cr2', 'nef', 'heic', 'heif'].includes(ext)) {
    return Image;
  }
  
  // 视频
  if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v', 'mpg', 'mpeg', '3gp'].includes(ext)) {
    return Clapperboard;
  }
  
  // 音频
  if (['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma', 'aiff', 'opus'].includes(ext)) {
    return Music;
  }
  
  // 代码文件
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp', 'cs', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'vue', 'svelte', 'dart', 'lua', 'r', 'pl', 'pm'].includes(ext)) {
    return FileCode2;
  }
  
  // Web 文件
  if (['html', 'htm', 'css', 'scss', 'sass', 'less'].includes(ext)) {
    return Code;
  }
  
  // 配置/数据文件
  if (['json', 'xml', 'yaml', 'yml', 'toml', 'ini', 'conf', 'cfg', 'env'].includes(ext)) {
    return FileJson;
  }
  
  // Shell/脚本
  if (['sh', 'bash', 'zsh', 'fish', 'ps1', 'bat', 'cmd'].includes(ext)) {
    return Terminal;
  }
  
  // 数据库
  if (['sql', 'db', 'sqlite', 'sqlite3', 'mdb', 'accdb'].includes(ext)) {
    return Database;
  }
  
  // Markdown/文本
  if (['md', 'markdown', 'txt', 'text', 'log'].includes(ext)) {
    return BookOpen;
  }
  
  // 字体
  if (['ttf', 'otf', 'woff', 'woff2', 'eot'].includes(ext)) {
    return FileType2;
  }
  
  // CAD/工程
  if (['dwg', 'dxf', 'dwf', 'step', 'stp', 'iges', 'igs'].includes(ext)) {
    return Layers;
  }
  
  // 3D 模型
  if (['obj', 'fbx', 'stl', 'gltf', 'glb', '3ds', 'blend', 'max', 'maya', 'c4d'].includes(ext)) {
    return Box;
  }
  
  // 设计文件
  if (['sketch', 'fig', 'xd', 'indd', 'afdesign', 'afphoto'].includes(ext)) {
    return Layers;
  }
  
  // 电子书
  if (['epub', 'mobi', 'azw', 'azw3', 'fb2', 'djvu'].includes(ext)) {
    return BookOpen;
  }
  
  // 可执行文件
  if (['exe', 'msi', 'dmg', 'pkg', 'deb', 'rpm', 'app', 'apk', 'ipa'].includes(ext)) {
    return FileCog;
  }
  
  // 证书/密钥
  if (['pem', 'crt', 'cer', 'key', 'p12', 'pfx', 'jks'].includes(ext)) {
    return FileKey;
  }
  
  // 默认
  return File;
};

// 获取文件图标的颜色配置
export const getFileIconColor = (filename: string): { bg: string; text: string } => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  
  // 压缩包 - 黄色
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz'].includes(ext)) {
    return { bg: 'bg-amber-50', text: 'text-amber-600' };
  }
  
  // PDF - 红色
  if (ext === 'pdf') {
    return { bg: 'bg-red-50', text: 'text-red-600' };
  }
  
  // Word - 蓝色
  if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) {
    return { bg: 'bg-blue-50', text: 'text-blue-600' };
  }
  
  // Excel - 绿色
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
    return { bg: 'bg-emerald-50', text: 'text-emerald-600' };
  }
  
  // PPT - 橙色
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) {
    return { bg: 'bg-orange-50', text: 'text-orange-600' };
  }
  
  // 图片 - 紫色
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico', 'tiff', 'tif', 'psd', 'ai', 'eps', 'raw', 'cr2', 'nef', 'heic', 'heif'].includes(ext)) {
    return { bg: 'bg-purple-50', text: 'text-purple-600' };
  }
  
  // 视频 - 粉色
  if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v', 'mpg', 'mpeg', '3gp'].includes(ext)) {
    return { bg: 'bg-pink-50', text: 'text-pink-600' };
  }
  
  // 音频 - 青色
  if (['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma', 'aiff', 'opus'].includes(ext)) {
    return { bg: 'bg-cyan-50', text: 'text-cyan-600' };
  }
  
  // 代码 - 灰色
  if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'h', 'hpp', 'cs', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'vue', 'svelte', 'dart', 'lua', 'r', 'pl', 'pm', 'html', 'htm', 'css', 'scss', 'sass', 'less'].includes(ext)) {
    return { bg: 'bg-slate-100', text: 'text-slate-700' };
  }
  
  // JSON/配置 - 黄绿色
  if (['json', 'xml', 'yaml', 'yml', 'toml', 'ini', 'conf', 'cfg', 'env'].includes(ext)) {
    return { bg: 'bg-lime-50', text: 'text-lime-700' };
  }
  
  // 数据库 - 靛蓝色
  if (['sql', 'db', 'sqlite', 'sqlite3', 'mdb', 'accdb'].includes(ext)) {
    return { bg: 'bg-indigo-50', text: 'text-indigo-600' };
  }
  
  // Markdown/文本 - 天蓝色
  if (['md', 'markdown', 'txt', 'text', 'log'].includes(ext)) {
    return { bg: 'bg-sky-50', text: 'text-sky-600' };
  }
  
  // 3D/CAD - 紫罗兰色
  if (['obj', 'fbx', 'stl', 'gltf', 'glb', '3ds', 'blend', 'max', 'maya', 'c4d', 'dwg', 'dxf', 'dwf', 'step', 'stp', 'iges', 'igs'].includes(ext)) {
    return { bg: 'bg-violet-50', text: 'text-violet-600' };
  }
  
  // 可执行文件 - 石板色
  if (['exe', 'msi', 'dmg', 'pkg', 'deb', 'rpm', 'app', 'apk', 'ipa'].includes(ext)) {
    return { bg: 'bg-stone-100', text: 'text-stone-600' };
  }
  
  // 默认 - 灰色
  return { bg: 'bg-gray-50', text: 'text-gray-500' };
};

// 保留原有的 getFileIcon 函数以保持兼容性
export const getFileIcon = (type: FileType): LucideIcon => {
  switch (type) {
    case 'document':
      return FileText;
    case 'image':
      return Image;
    case 'video':
      return Clapperboard;
    case 'audio':
      return Music;
    case 'pdf':
      return FileText;
    case 'spreadsheet':
      return FileSpreadsheet;
    case 'presentation':
      return Presentation;
    case 'code':
      return FileCode2;
    default:
      return File;
  }
};

export const getFileExtension = (filename: string): string => {
  return filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
};

export const getFileTypeFromExtension = (extension: string): FileType => {
  const ext = extension.toLowerCase();
  
  if (['doc', 'docx', 'rtf'].includes(ext)) {
    return 'document';
  }
  
  // txt 和 md 归类为 code 以便使用代码/文本预览
  if (['txt', 'md', 'markdown'].includes(ext)) {
    return 'code';
  }
  
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
    return 'image';
  }
  
  if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) {
    return 'video';
  }
  
  if (['mp3', 'wav', 'ogg'].includes(ext)) {
    return 'audio';
  }
  
  if (ext === 'pdf') {
    return 'pdf';
  }
  
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return 'spreadsheet';
  }
  
  if (['ppt', 'pptx'].includes(ext)) {
    return 'presentation';
  }
  
  if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'py', 'java', 'c', 'cpp', 'json', 'xml', 'yaml', 'yml', 'sh', 'bash', 'sql', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'scala', 'vue', 'svelte'].includes(ext)) {
    return 'code';
  }
  
  return 'other';
};

// 根据文件扩展名获取语法高亮语言
export const getLanguageFromExtension = (filename: string): string => {
  const ext = getFileExtension(filename).toLowerCase();
  
  const languageMap: Record<string, string> = {
    'js': 'javascript',
    'jsx': 'jsx',
    'ts': 'typescript',
    'tsx': 'tsx',
    'py': 'python',
    'java': 'java',
    'c': 'c',
    'cpp': 'cpp',
    'cs': 'csharp',
    'go': 'go',
    'rs': 'rust',
    'rb': 'ruby',
    'php': 'php',
    'swift': 'swift',
    'kt': 'kotlin',
    'scala': 'scala',
    'html': 'html',
    'css': 'css',
    'scss': 'scss',
    'less': 'less',
    'json': 'json',
    'xml': 'xml',
    'yaml': 'yaml',
    'yml': 'yaml',
    'md': 'markdown',
    'markdown': 'markdown',
    'sql': 'sql',
    'sh': 'bash',
    'bash': 'bash',
    'zsh': 'bash',
    'txt': 'text',
    'vue': 'vue',
    'svelte': 'svelte',
  };
  
  return languageMap[ext] || 'text';
};

// 检查文件是否为 Markdown
export const isMarkdownFile = (filename: string): boolean => {
  const ext = getFileExtension(filename).toLowerCase();
  return ['md', 'markdown'].includes(ext);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const mb = bytes / (1024 * 1024);
  
  if (mb < 0.1) {
    return '< 0.1 MB';
  }
  
  return `${mb.toFixed(1)} MB`;
};